/**
 * Crew ▸ Crew Profile ve "+ Add Crew" (personel kartı; ortak 2026-10-27; ADR 0131 §1, §2, Ek 1):
 * `gemi-personel.jpeg` panel 3. Kart ofiste açılır ve düzenlenir; gemide salt okunur ve iletişim
 * bilgisi yoktur (KVKK md. 4). Giriş hesabı isteğe bağlıdır ("Create login").
 */

export const crewProfileEn = {
  title: 'Crew Profile',
  back: 'Crew List',
  error: 'The crew profile could not be loaded.',
  notFound: 'This person was not found.',
  edit: 'Edit',
  more: 'More',
  moreFor: 'More actions for {{name}}',
  createLogin: 'Create login',
  removeCard: 'Remove card',
  resetPassword: 'Reset password',
  // Kartın üstündeki açık düğme (ortak 2026-10-30 madde 9): reset-password akışı
  newPassword: 'New password',
  // Sign On sonrası hesap önerisi (ortak 2026-10-30 madde 9)
  loginOffer: {
    text: '{{name}} has no login yet.',
    create: 'Create login now',
    skip: 'Not now',
  },
  // Kartta hesap işlemleri (ortak 2026-10-30): Settings ▸ Users'daki aynı uçlar
  account: {
    active: 'Login active',
    disabled: 'Login disabled',
    inactive: 'Disabled',
    mustChange: 'Must change password',
    changeEmail: 'Change login email',
    disable: 'Disable login',
    enable: 'Enable login',
    // Kişinin iki adımlı doğrulamasını kapatır (ADR 0148); kişi isterse Ayarlar ▸ Hesap'tan açar
    resetTotp: 'Turn off two-step verification',
  },
  signOn: 'Sign on',
  signOff: 'Sign off',
  noLogin: 'No login',
  noLoginHelp: 'This person has no account and cannot sign in. Use "Create login" to open one.',
  openCertificates: 'Certificates & Documents',
  openRestHours: 'Hours of Work & Rest',
  openProfile: 'Crew Profile',
  shipReadOnly:
    'Crew cards are kept by the office. On board the card is read-only. If you forgot your password, ask the office to reset it; the new password works on board after the next sync.',
  print: 'Print',
  printFor: 'Print the crew profile of {{name}}',
  downloadPdf: 'Download PDF',
  pdfFailed: 'The PDF could not be produced. Try again.',
  header: {
    login: 'Login',
    phone: 'Phone',
    nationality: 'Nationality',
    dateOfBirth: 'Date of Birth',
    crewCode: 'Crew ID',
  },
  status: { on_board: 'On Board', ashore: 'Ashore', not_assigned: 'Not assigned' },
  tabs: {
    label: 'Crew profile sections',
    overview: 'Overview',
    contracts: 'Contracts',
    joining: 'Joining / Sign-Off',
    documents: 'Documents',
    training: 'Training',
  },
  sections: {
    personal: 'Personal Information',
    contact: 'Contact Information',
    emergency: 'Emergency Contact',
    physical: 'Clothing & Physical',
    medical: 'Medical Information',
  },
  fields: {
    fullName: 'Full Name',
    crewCode: 'Crew ID',
    rank: 'Rank',
    vessel: 'Vessel',
    nationality: 'Nationality',
    dateOfBirth: 'Date of Birth',
    placeOfBirth: 'Place of Birth',
    gender: 'Gender',
    maritalStatus: 'Marital Status',
    phone: 'Phone',
    contactEmail: 'Email',
    address: 'Address',
    emergencyName: 'Name',
    emergencyRelation: 'Relation',
    emergencyPhone: 'Phone',
    coverallSize: 'Coverall size',
    upperSize: 'Shirt / jacket size',
    lowerSize: 'Trousers size',
    shoeSize: 'Shoe size',
    heightCm: 'Height (cm)',
    weightKg: 'Weight (kg)',
    eyeColor: 'Eye colour',
    hairColor: 'Hair colour',
    distinguishingMarks: 'Distinguishing marks',
    bloodGroup: 'Blood group',
    medicalHistory: 'Medical history',
    medications: 'Medications in use',
    allergies: 'Allergies',
  },
  dialogTabs: {
    personal: 'Personal',
    physical: 'Clothing & physical',
    medical: 'Medical',
  },
  photo: {
    label: 'Photo',
    upload: 'Upload photo',
    change: 'Change photo',
    remove: 'Remove photo',
    help: 'JPEG, PNG or WebP, up to 5 MB. Large photos are reduced before upload.',
    needsCard: 'Save the card first, then add a photo.',
  },
  physical: {
    clothing: 'Clothing',
    physical: 'Physical characteristics',
    sizePlaceholder: 'S, M, L, XL or 52',
    shoePlaceholder: 'EU 43',
    heightHelp: 'Whole centimetres, 50–250.',
    weightHelp: 'Kilograms, 20–300; one decimal allowed.',
    cm: '{{value}} cm',
    kg: '{{value}} kg',
  },
  medical: {
    notice:
      'Health data is special category personal data (KVKK art. 6). Only authorised users see it, and every view is recorded.',
    hiddenHelp: 'Hidden until requested. Opening it is recorded in the change history.',
    show: 'Show medical information',
    hide: 'Hide',
    loading: 'Loading medical information',
    empty: 'No medical information recorded.',
    readOnly: 'You can view this information but not change it.',
    save: 'Save medical information',
    saved: 'Medical information saved.',
  },
  gender: { M: 'Male', F: 'Female', X: 'Unspecified' },
  marital: { single: 'Single', married: 'Married', other: 'Other' },
  assignments: {
    contractsTable: 'Contracts',
    joiningTable: 'Joining and sign-off',
    vessel: 'Vessel',
    rank: 'Rank',
    start: 'Start Date',
    end: 'End Date',
    joined: 'Joining Date',
    signedOff: 'Sign-Off Date',
    status: 'Status',
    contractNo: 'Contract No',
    joinPort: 'Joining Port',
    signOffPort: 'Sign-Off Port',
    reason: 'Reason',
    actions: 'Actions',
    newContract: 'New Contract',
    state: { active: 'Active', completed: 'Completed', planned: 'Planned', overdue: 'Overdue' },
    empty: 'No vessel assignment yet.',
    readOnly: 'Sign on and sign off are recorded by the office in Sign On / Off.',
  },
  dialog: {
    newTitle: 'New Crew Card',
    editTitle: 'Edit Crew Card',
    help: 'Opens the crew card. A login account is optional; it can also be created later.',
    fullName: 'Full name',
    crewCode: 'Crew ID',
    crewCodePlaceholder: 'Auto',
    crewCodeHelp: 'Left empty, the next number is given (C-001, C-002 …).',
    nationality: 'Nationality',
    nationalityPlaceholder: 'Search country',
    dateOfBirth: 'Date of Birth',
    placeOfBirth: 'Place of Birth',
    gender: 'Gender',
    maritalStatus: 'Marital Status',
    phone: 'Phone',
    contactEmail: 'Email (contact)',
    contactEmailHelp: 'For contact only; it is not a sign-in address.',
    address: 'Address',
    emergency: 'Emergency Contact',
    emergencyName: 'Name',
    emergencyRelation: 'Relation',
    emergencyPhone: 'Phone',
    signOnNow: 'Sign on now',
    signOnNowHelp:
      'Records the joining together with the card. Nationality and date of birth are required.',
    createLogin: 'Create login',
    createLoginHelp:
      'Opens a sign-in account with a temporary password. The password is shown once.',
    loginEmail: 'Login email',
    password: 'Your password',
    passwordHelp: 'Confirm with your own password to open an account.',
    cancel: 'Cancel',
    close: 'Close',
    create: 'Create Crew Card',
    save: 'Save',
    created: 'Crew card created',
    loginTitle: 'Create login',
    loginHelp: 'Opens a sign-in account for {{name}} with a temporary password.',
    // Yetki adlandırılmaz: yönetici hesabını Users ▸ Void yetmez, yalnız şirket yöneticisi
    // etkinleştirir (U39; ADR 0150 §3)
    loginInactive:
      '{{name}} stays disabled: the login cannot be used until an authorized user enables the account.',
    loginSubmit: 'Create login',
    removeTitle: 'Remove the card of {{name}}',
    removeHelp:
      'Use this only for a card opened by mistake: {{name}} has never been assigned to a vessel. The card leaves the crew list; the change history is kept.',
    removePasswordHelp: 'Confirm with your own password.',
    removeSubmit: 'Remove card',
  },
  pdf: {
    title: 'Crew Profile',
    currentContract: 'Current Contract',
    contracts: 'Contracts and Sign On / Off',
    documents: 'Certificates and Documents',
    shipCopy: 'Vessel copy: contact and emergency details are kept by the office.',
    noDocuments: 'No certificate or document recorded.',
    documentsUnavailable: 'Documents could not be read; see Crew › Certificates & Documents.',
    missing: 'Missing for the rank: {{list}}',
    summary: '{{total}} documents · {{valid}} valid · {{expiring}} expiring · {{expired}} expired',
    columns: {
      contractNo: 'Contract No',
      vessel: 'Vessel',
      rank: 'Rank',
      joined: 'Joining',
      signedOff: 'Sign-Off',
      contractEnd: 'Contract end',
      status: 'Status',
      document: 'Document',
      number: 'Number',
      issued: 'Issued',
      expires: 'Expires',
      issuedBy: 'Issued by',
    },
    docStatus: { valid: 'Valid', expiring: 'Expiring', expired: 'Expired' },
  },
  employment: {
    action: 'Record of Employment',
    actionFor: 'Record of Employment for {{vessel}}',
    title: 'Record of Employment',
    subtitle: 'Record of service on board · MLC 2006 Standard A2.1 §1(e), §3',
    seafarer: 'Seafarer',
    ship: 'Ship',
    service: 'Service on board',
    fields: {
      fullName: 'Full name',
      crewCode: 'Crew ID',
      dateOfBirth: 'Date of birth',
      placeOfBirth: 'Place of birth',
      nationality: 'Nationality',
      document: 'Identity document',
      vesselName: 'Name of ship',
      imo: 'IMO number',
      flag: 'Flag',
      rank: 'Capacity (rank)',
      joined: 'Date of joining',
      joinPort: 'Port of joining',
      signedOff: 'Date of discharge',
      signOffPort: 'Port of discharge',
    },
    docType: { SBOOK: "Seaman's Book", PASSPORT: 'Passport' },
    statement:
      "Issued under MLC 2006 Standard A2.1 §1(e) and §3. It records the seafarer's service on board only and contains no assessment of the seafarer's work.",
    signatureMaster: 'Master — name, signature, date',
    signatureCompany: 'Company representative — name, signature, date',
    failed: 'The record could not be produced. Try again.',
  },
  errors: {
    VALIDATION_FAILED: 'Check the marked fields.',
    INVALID_NATIONALITY: 'Choose a nationality from the list.',
    INVALID_LOCAL_DATE: 'Enter a valid date of birth.',
    FUTURE_DATE: 'The date of birth cannot be in the future.',
    INVALID_EMAIL: 'Enter a valid email address.',
    EMAIL_TAKEN: 'This login email is already in use.',
    DUPLICATE_CREW_CODE: 'This Crew ID is already used by another person.',
    CREW_PROFILE_EXISTS: 'This person already has a crew card.',
    LOGIN_EXISTS: 'This person already has a login.',
    ROLE_KIND_MISMATCH: 'An office title cannot be a rank. Choose a vessel rank.',
    PERSON_HAS_LOGIN: 'This person has a login. Disable the account in Settings › Users instead.',
    PERSON_HAS_RECORDS:
      'This person has assignments or records (rest hours, payroll, drills). The card cannot be removed.',
    ROW_VERSION_CONFLICT: 'The card was changed by someone else. Reload and try again.',
    REAUTHENTICATION_REQUIRED: 'Enter your password.',
    INVALID_PASSWORD: 'The password is not correct.',
    FORBIDDEN: 'You do not have permission for this.',
    NOT_FOUND: 'This person was not found.',
    CREW_MEDICAL_EXISTS: 'Medical information was already recorded. Reload and try again.',
    FILE_TOO_LARGE: 'The photo is larger than 5 MB.',
    FILE_TYPE_MISMATCH: 'Choose a JPEG, PNG or WebP photo.',
    UPLOAD_PURPOSE_MISMATCH: 'Upload the photo again.',
    UPLOAD_CHANGED: 'Upload the photo again.',
    FILES_UNAVAILABLE: 'File storage is not available right now.',
    NETWORK: 'The connection was interrupted. Try again.',
    UPLOAD_FAILED: 'The photo could not be uploaded. Try again.',
    GENERIC: 'The crew card could not be saved. Try again.',
  },
};

export const crewProfileTr: typeof crewProfileEn = {
  title: 'Personel Kartı',
  back: 'Personel Listesi',
  error: 'Personel kartı yüklenemedi.',
  notFound: 'Bu kişi bulunamadı.',
  edit: 'Düzenle',
  more: 'Diğer',
  moreFor: '{{name}} için diğer işlemler',
  createLogin: 'Giriş hesabı aç',
  removeCard: 'Kartı kaldır',
  resetPassword: 'Parolayı sıfırla',
  newPassword: 'Yeni parola ver',
  loginOffer: {
    text: '{{name}} için henüz giriş yok.',
    create: 'Şimdi giriş aç',
    skip: 'Şimdi değil',
  },
  account: {
    active: 'Giriş etkin',
    disabled: 'Giriş devre dışı',
    inactive: 'Devre dışı',
    mustChange: 'Parolasını değiştirmeli',
    changeEmail: 'Giriş e-postasını değiştir',
    disable: 'Girişi devre dışı bırak',
    enable: 'Girişi etkinleştir',
    resetTotp: 'İki adımlı doğrulamayı kapat',
  },
  signOn: 'Katılış kaydet',
  signOff: 'Ayrılış kaydet',
  noLogin: 'Giriş hesabı yok',
  noLoginHelp:
    'Bu kişinin hesabı yok ve giriş yapamaz. Hesap açmak için "Giriş hesabı aç"ı kullanın.',
  openCertificates: 'Sertifikalar ve Belgeler',
  openRestHours: 'Çalışma ve Dinlenme Saatleri',
  openProfile: 'Personel Kartı',
  shipReadOnly:
    'Personel kartlarını ofis tutar. Gemide kart salt okunurdur. Parolanızı unuttuysanız ofisten sıfırlama isteyin; yeni parola bir sonraki senkronda gemide geçerli olur.',
  print: 'Yazdır',
  printFor: '{{name}} personel kartını yazdır',
  downloadPdf: 'PDF indir',
  pdfFailed: 'PDF oluşturulamadı. Yeniden deneyin.',
  header: {
    login: 'Giriş hesabı',
    phone: 'Telefon',
    nationality: 'Uyruk',
    dateOfBirth: 'Doğum Tarihi',
    crewCode: 'Personel No',
  },
  status: { on_board: 'Gemide', ashore: 'Karada', not_assigned: 'Atanmadı' },
  tabs: {
    label: 'Personel kartı bölümleri',
    overview: 'Genel',
    contracts: 'Kontratlar',
    joining: 'Katılış / Ayrılış',
    documents: 'Belgeler',
    training: 'Eğitim',
  },
  sections: {
    personal: 'Kişisel Bilgiler',
    contact: 'İletişim Bilgileri',
    emergency: 'Acil Durum Kişisi',
    physical: 'Kıyafet ve Fiziksel Özellikler',
    medical: 'Sağlık Bilgisi',
  },
  fields: {
    fullName: 'Ad Soyad',
    crewCode: 'Personel No',
    rank: 'Rütbe',
    vessel: 'Gemi',
    nationality: 'Uyruk',
    dateOfBirth: 'Doğum Tarihi',
    placeOfBirth: 'Doğum Yeri',
    gender: 'Cinsiyet',
    maritalStatus: 'Medeni Hal',
    phone: 'Telefon',
    contactEmail: 'E-posta',
    address: 'Adres',
    emergencyName: 'Ad Soyad',
    emergencyRelation: 'Yakınlık',
    emergencyPhone: 'Telefon',
    coverallSize: 'Tulum bedeni',
    upperSize: 'Üst beden (gömlek / ceket)',
    lowerSize: 'Alt beden (pantolon)',
    shoeSize: 'Ayakkabı numarası',
    heightCm: 'Boy (cm)',
    weightKg: 'Kilo (kg)',
    eyeColor: 'Göz rengi',
    hairColor: 'Saç rengi',
    distinguishingMarks: 'Ayırt edici işaretler',
    bloodGroup: 'Kan grubu',
    medicalHistory: 'Hastalık geçmişi',
    medications: 'Kullandığı ilaçlar',
    allergies: 'Alerjiler',
  },
  dialogTabs: {
    personal: 'Kişisel',
    physical: 'Kıyafet ve fiziksel',
    medical: 'Sağlık',
  },
  photo: {
    label: 'Fotoğraf',
    upload: 'Fotoğraf yükle',
    change: 'Fotoğrafı değiştir',
    remove: 'Fotoğrafı kaldır',
    help: 'JPEG, PNG ya da WebP, en çok 5 MB. Büyük fotoğraf yüklenmeden önce küçültülür.',
    needsCard: 'Önce kartı kaydedin, sonra fotoğraf ekleyin.',
  },
  physical: {
    clothing: 'Kıyafet',
    physical: 'Fiziksel özellikler',
    sizePlaceholder: 'S, M, L, XL ya da 52',
    shoePlaceholder: 'EU 43',
    heightHelp: 'Tam santimetre, 50–250.',
    weightHelp: 'Kilogram, 20–300; bir ondalık olabilir.',
    cm: '{{value}} cm',
    kg: '{{value}} kg',
  },
  medical: {
    notice:
      'Sağlık bilgisi özel nitelikli kişisel veridir (KVKK md. 6). Yalnız yetkili kişiler görür; her görüntüleme kayda geçer.',
    hiddenHelp: 'İstenene kadar gizli. Açılması değişiklik kaydına yazılır.',
    show: 'Sağlık bilgisini göster',
    hide: 'Gizle',
    loading: 'Sağlık bilgisi yükleniyor',
    empty: 'Kayıtlı sağlık bilgisi yok.',
    readOnly: 'Bu bilgiyi görebilirsiniz, değiştiremezsiniz.',
    save: 'Sağlık bilgisini kaydet',
    saved: 'Sağlık bilgisi kaydedildi.',
  },
  gender: { M: 'Erkek', F: 'Kadın', X: 'Belirtilmemiş' },
  marital: { single: 'Bekâr', married: 'Evli', other: 'Diğer' },
  assignments: {
    contractsTable: 'Kontratlar',
    joiningTable: 'Katılış ve ayrılış',
    vessel: 'Gemi',
    rank: 'Rütbe',
    start: 'Başlangıç',
    end: 'Bitiş',
    joined: 'Katılış Tarihi',
    signedOff: 'Ayrılış Tarihi',
    status: 'Durum',
    contractNo: 'Kontrat No',
    joinPort: 'Katılış Limanı',
    signOffPort: 'Ayrılış Limanı',
    reason: 'Neden',
    actions: 'İşlemler',
    newContract: 'Yeni Kontrat',
    state: { active: 'Etkin', completed: 'Tamamlandı', planned: 'Planlı', overdue: 'Süresi geçti' },
    empty: 'Henüz gemi ataması yok.',
    readOnly: 'Katılış ve ayrılışı ofis Katılış / Ayrılış ekranında kaydeder.',
  },
  dialog: {
    newTitle: 'Yeni Personel Kartı',
    editTitle: 'Personel Kartını Düzenle',
    help: 'Personel kartını açar. Giriş hesabı isteğe bağlıdır; sonradan da açılabilir.',
    fullName: 'Ad soyad',
    crewCode: 'Personel No',
    crewCodePlaceholder: 'Otomatik',
    crewCodeHelp: 'Boş bırakılırsa sıradaki numara verilir (C-001, C-002 …).',
    nationality: 'Uyruk',
    nationalityPlaceholder: 'Ülke arayın',
    dateOfBirth: 'Doğum Tarihi',
    placeOfBirth: 'Doğum Yeri',
    gender: 'Cinsiyet',
    maritalStatus: 'Medeni Hal',
    phone: 'Telefon',
    contactEmail: 'E-posta (iletişim)',
    contactEmailHelp: 'Yalnız iletişim içindir; giriş adresi değildir.',
    address: 'Adres',
    emergency: 'Acil Durum Kişisi',
    emergencyName: 'Ad Soyad',
    emergencyRelation: 'Yakınlık',
    emergencyPhone: 'Telefon',
    signOnNow: 'Gemiye katılışı da kaydet',
    signOnNowHelp: 'Katılışı kartla birlikte kaydeder. Uyruk ve doğum tarihi zorunludur.',
    createLogin: 'Giriş hesabı aç',
    createLoginHelp: 'Geçici parolayla bir giriş hesabı açar. Parola bir kez gösterilir.',
    loginEmail: 'Giriş e-postası',
    password: 'Parolanız',
    passwordHelp: 'Hesap açmak için kendi parolanızla onaylayın.',
    cancel: 'Vazgeç',
    close: 'Kapat',
    create: 'Personel Kartını Aç',
    save: 'Kaydet',
    created: 'Personel kartı açıldı',
    loginTitle: 'Giriş hesabı aç',
    loginHelp: '{{name}} için geçici parolayla bir giriş hesabı açar.',
    loginInactive:
      '{{name}} devre dışı kalır: yetkili biri hesabı etkinleştirene dek giriş yapamaz.',
    loginSubmit: 'Hesabı aç',
    removeTitle: '{{name}} kartını kaldır',
    removeHelp:
      'Yalnız yanlışlıkla açılan kart için: {{name}} hiçbir gemiye atanmadı. Kart personel listesinden çıkar; değişiklik geçmişi kalır.',
    removePasswordHelp: 'Kendi parolanızla onaylayın.',
    removeSubmit: 'Kartı kaldır',
  },
  pdf: {
    title: 'Personel Kartı',
    currentContract: 'Süren Kontrat',
    contracts: 'Kontratlar ve Katılış / Ayrılış',
    documents: 'Sertifikalar ve Belgeler',
    shipCopy: 'Gemi kopyası: iletişim ve acil durum bilgilerini ofis tutar.',
    noDocuments: 'Kayıtlı sertifika ya da belge yok.',
    documentsUnavailable:
      'Belgeler okunamadı; Personel › Sertifikalar ve Belgeler sayfasına bakın.',
    missing: 'Rütbe için eksik: {{list}}',
    summary:
      '{{total}} belge · {{valid}} geçerli · {{expiring}} süresi yaklaşan · {{expired}} süresi geçmiş',
    columns: {
      contractNo: 'Kontrat No',
      vessel: 'Gemi',
      rank: 'Rütbe',
      joined: 'Katılış',
      signedOff: 'Ayrılış',
      contractEnd: 'Kontrat bitişi',
      status: 'Durum',
      document: 'Belge',
      number: 'Numara',
      issued: 'Veriliş',
      expires: 'Bitiş',
      issuedBy: 'Veren',
    },
    docStatus: { valid: 'Geçerli', expiring: 'Süresi yaklaşıyor', expired: 'Süresi geçti' },
  },
  employment: {
    action: 'Çalışma Kaydı (Record of Employment)',
    actionFor: '{{vessel}} için çalışma kaydı',
    title: 'Record of Employment / Gemide Çalışma Kaydı',
    subtitle: 'Gemideki hizmetin kaydı · MLC 2006 Standard A2.1 §1(e), §3',
    seafarer: 'Seafarer / Gemiadamı',
    ship: 'Ship / Gemi',
    service: 'Service on board / Gemideki hizmet',
    fields: {
      fullName: 'Full name / Adı soyadı',
      crewCode: 'Crew ID / Personel no',
      dateOfBirth: 'Date of birth / Doğum tarihi',
      placeOfBirth: 'Place of birth / Doğum yeri',
      nationality: 'Nationality / Uyruk',
      document: 'Identity document / Kimlik belgesi',
      vesselName: 'Name of ship / Gemi adı',
      imo: 'IMO number / IMO numarası',
      flag: 'Flag / Bayrak',
      rank: 'Capacity (rank) / Görevi (rütbe)',
      joined: 'Date of joining / Katılış tarihi',
      joinPort: 'Port of joining / Katılış limanı',
      signedOff: 'Date of discharge / Ayrılış tarihi',
      signOffPort: 'Port of discharge / Ayrılış limanı',
    },
    docType: { SBOOK: "Seaman's Book / Gemiadamı cüzdanı", PASSPORT: 'Passport / Pasaport' },
    statement:
      'MLC 2006 Standard A2.1 §1(e) ve §3 uyarınca verilir. Yalnız gemideki hizmeti kaydeder; gemiadamının işi hakkında değerlendirme içermez.',
    signatureMaster: 'Master / Kaptan — ad, imza, tarih',
    signatureCompany: 'Company representative / Şirket temsilcisi — ad, imza, tarih',
    failed: 'Kayıt oluşturulamadı. Yeniden deneyin.',
  },
  errors: {
    VALIDATION_FAILED: 'İşaretli alanları kontrol edin.',
    INVALID_NATIONALITY: 'Listeden bir uyruk seçin.',
    INVALID_LOCAL_DATE: 'Geçerli bir doğum tarihi girin.',
    FUTURE_DATE: 'Doğum tarihi ileri bir tarih olamaz.',
    INVALID_EMAIL: 'Geçerli bir e-posta adresi girin.',
    EMAIL_TAKEN: 'Bu giriş e-postası kullanılıyor.',
    DUPLICATE_CREW_CODE: 'Bu personel numarası başka bir kişide.',
    CREW_PROFILE_EXISTS: 'Bu kişinin personel kartı zaten var.',
    LOGIN_EXISTS: 'Bu kişinin giriş hesabı zaten var.',
    ROLE_KIND_MISMATCH: 'Ofis ünvanı rütbe olarak verilemez. Bir gemi rütbesi seçin.',
    PERSON_HAS_LOGIN: "Bu kişinin giriş hesabı var. Hesabı Ayarlar › Kullanıcılar'dan kapatın.",
    PERSON_HAS_RECORDS:
      'Bu kişinin ataması ya da kaydı var (dinlenme saati, bordro, tatbikat). Kart kaldırılamaz.',
    ROW_VERSION_CONFLICT: 'Kartı başka biri değiştirdi. Yenileyip yeniden deneyin.',
    REAUTHENTICATION_REQUIRED: 'Parolanızı girin.',
    INVALID_PASSWORD: 'Parola doğru değil.',
    FORBIDDEN: 'Bu işlem için yetkiniz yok.',
    NOT_FOUND: 'Bu kişi bulunamadı.',
    CREW_MEDICAL_EXISTS: 'Sağlık bilgisi zaten girilmiş. Yenileyip yeniden deneyin.',
    FILE_TOO_LARGE: 'Fotoğraf 5 MB’tan büyük.',
    FILE_TYPE_MISMATCH: 'JPEG, PNG ya da WebP bir fotoğraf seçin.',
    UPLOAD_PURPOSE_MISMATCH: 'Fotoğrafı yeniden yükleyin.',
    UPLOAD_CHANGED: 'Fotoğrafı yeniden yükleyin.',
    FILES_UNAVAILABLE: 'Dosya deposu şu an kullanılamıyor.',
    NETWORK: 'Bağlantı kesildi. Yeniden deneyin.',
    UPLOAD_FAILED: 'Fotoğraf yüklenemedi. Yeniden deneyin.',
    GENERIC: 'Personel kartı kaydedilemedi. Yeniden deneyin.',
  },
};
